<script setup>
import { parseTime } from "@/utils/ruoyi";
import { signTimeData, exportDeviceData } from "@/api/device";
import { nextTick, ref } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import * as echarts from "echarts";
import { useI18n } from "vue-i18n";

// 引入i18n用于国际化
const { t } = useI18n();

// 定义引用变量，用于图表和DOM元素的操作
const main = ref(null);
const attributeValue = ref(null);
const datePicker = ref();

// 接收props
const props = defineProps({
  argList: {
    type: Array,
    default: () => [{ name: "--", value: "--", unit: "--", url: "" }],
  },
  argObj: {
    type: Object,
  },
  deviceName: {
    type: String,
  },
});

// 定义变量，管理对话框显示状态、日期和当前选择的属性项
const dialogVisible = ref(false);
const dateValue = ref("");
const currentSignItem = ref({});
const currentArg = ref(null);

// 处理对话框关闭事件
const handleClose = (done) => {
  ElMessageBox.confirm(t("arg.closeDialog"), {
    confirmButtonText: t("group.confirm"),
    cancelButtonText: t("group.cancel"),
    type: "warning",
  })
    .then(() => {
      done();
    })
    .catch(() => {
      // 处理取消操作
    });
};

const chartContainer = `main-${Date.now()}`;
// 检查属性数据
const checkArgData = (item, key) => {
  currentArg.value = item;
  currentLineData.value = null;
  dialogVisible.value = true;
  currentSignItem.value = key;
  dateValue.value = null;
};

// 获取日期范围
const getDate = (value) => {
  if (!value) {
    return;
  }
  const oneMonthInMs = 30 * 24 * 60 * 60 * 1000; // 30天的毫秒数
  const timeDiff = Math.abs(value[0] - value[1]);
  if(timeDiff > oneMonthInMs){
  dateValue.value = null;
    ElMessage({
        message: t("arg.noTimeRange"),
        type: "error",
      });
  } 
  
};

const argData = ref(false);

// 查询当前设备属性历史数据
const onSubmit = () => {
  let params = {};
  if (dateValue.value) {
    const [startTime, endTime] = dateValue.value;
    if (!startTime || !endTime) {
      ElMessage({
        message: t("arg.noTime"),
        type: "error",
      });
      return;
    }

    if (
      attributeValue.value == null ||
      Object.keys(attributeValue.value).length === 0
    ) {
      ElMessage({
        message: t("arg.noAtr"),
        type: "error",
      });
      return;
    }

    params = {
      // expansionTime: 5,
      deviceName: props.deviceName,
      startTime,
      endTime,
      yeuSignList: [...attributeValue.value],
    };
  } else {
    ElMessage({
      message: t("arg.noTime"),
      type: "error",
    });
    return;
  }

  // 请求设备历史数据
  signTimeData(params).then((res) => {
    if (res && res.data && res.data.length > 0) {
      res.data = res.data.map((item) => {
        item["collect_time"] = parseTime(item["collect_time"]);
        return item;
      });

      currentLineData.value = res.data;
      argData.value = true;
      // 更新图表数据
      nextTick(() => {
        let myChart = echarts.init(document.getElementById(chartContainer));
        
        const selectedAttrs = Array.isArray(attributeValue.value) ? attributeValue.value : [currentSignItem.value];
        
        const series = selectedAttrs.map((attrKey) => {
          const attrItem = props.argList[attrKey];
          return {
            name: attrItem?.name || attrKey,
            data: res.data.map((item) => item[attrKey] || 0),
            type: "line",
            smooth: true,
          };
        });

        let option = {
          xAxis: {
            type: "category",
            data: res.data.map((item) => item["collect_time"]),
          },
          yAxis: {
            type: "value",
          },
          series,
          dataZoom: [
            {
              type: "inside",
              start: 0,
              end: 20,
            },
            {
              start: 0,
              end: 20,
            },
          ],
          tooltip: {
            trigger: "axis",
            formatter: function (params) {
              let result = `<div>${params[0].axisValue}</div>`;
              params.forEach((param) => {
                const attrKey = selectedAttrs.find((key) => {
                  const item = props.argList[key];
                  return item?.name === param.seriesName;
                });
                const unit = props.argList[attrKey]?.unit || "";
                result += `<div>${param.marker} ${param.seriesName}: ${param.value} ${unit}</div>`;
              });
              return result;
            },
          },
        };

        // 清除旧数据并重新设置图表
        myChart.setOption(option);
      });
    } else {
      argData.value = false;
    }
  });
};

const currentLineData = ref(null);

// 导出数据
const onDownload = () => {
  if (!dateValue.value) {
    ElMessage({
      message: t("arg.noTime"),
      type: "error",
    });
    return;
  }

  const [startTime, endTime] = dateValue.value;
  if (!startTime || !endTime) {
    ElMessage({
      message: t("arg.noTime"),
      type: "error",
    });
    return;
  }

  if (
    attributeValue.value == null ||
    Object.keys(attributeValue.value).length === 0
  ) {
    ElMessage({
      message: t("arg.noAtr"),
      type: "error",
    });
    return;
  }

  let params = {
    deviceName: props.deviceName,
    startTime,
    endTime,
    yeuSignList: [...attributeValue.value],
  };

  // 导出设备数据为Excel文件
  exportDeviceData(params)
    .then((res) => {
      const blob = new Blob([res], { type: "application/vnd.ms-excel" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "device_data.xlsx"; // 指定下载文件名
      link.click();
      URL.revokeObjectURL(link.href);
    })
    .catch((error) => {
      console.error("导出失败:", error);
    });
};

const disabledFutureDate = (time) => {
  return time.getTime() > Date.now();
};
</script>

<template>
  <div class="d-flex flex-wrap">
    <div
      class="col-lg-3 col-md-4 col-sm-12"
      v-for="(item, key) in props.argList"
      :key="key"
    >
      <div class="p-3 m-1 arg-container">
        <div class="d-flex align-items-center justify-content-around">
          <div class="p-2 d-flex flex-column">
            <div>
              {{ item.value }} {{ item.unit || "--" }}
              <!--            <span v-if="argObj && argObj[key] && argObj[key].yeuUnit">{{argObj[key].yeuUnit || '&#45;&#45;'}}</span>-->
            </div>
            <div>
              {{
                $t(`element.${item.name}`) !== `element.${item.name}`
                  ? $t(`element.${item.name}`)
                  : item.name
              }}
            </div>
          </div>
          <div>
            <img
              v-if="argObj && argObj[key] && argObj[key].yeuUrl"
              style="width: 30px; object-fit: contain"
              :src="argObj[key].yeuUrl"
              alt=""
              srcset=""
            />
            <span v-else>--</span>
          </div>
        </div>
        <div
          v-if="item && item.time"
          class="p-1 d-flex align-items-center justify-content-between"
        >
          <el-link>{{ parseTime(item.time) || "--" }}</el-link>
          <!--        <el-link type="primary" @click="$emit('toChart', item)">查看曲线</el-link>-->
          <el-link type="primary" @click="checkArgData(item, key)">{{
            $t("arg.viewCurve")
          }}</el-link>
        </div>
        <div v-else>--</div>
      </div>
    </div>

    <el-dialog
      style="z-index: 9999"
      v-model="dialogVisible"
      :title="$t('arg.historicalData')"
      width="60%"
      :before-close="handleClose"
    >
      <div>
        <el-select
          v-model="attributeValue"
          multiple
          collapse-tags
          style="margin-right: 20px; width: 200px"
          placeholder="请选择属性"
        >
          <el-option
            v-for="(item, key) of props.argList"
            :key="key"
            :label="item.name"
            :value="key"
          >
          </el-option>
        </el-select>
        <el-date-picker
          ref="datePicker"
          popper-class="custom-date-picker-popper"
          v-model="dateValue"
          type="datetimerange"
          :start-placeholder="$t('alarm.startTime')"
          :end-placeholder="$t('alarm.endTime')"
          format="YYYY-MM-DD HH:mm:ss"
          date-format="YYYY/MM/DD"
          value-format="x"
          time-format="A hh:mm:ss"
          :disabled-date="disabledFutureDate"
          @change="getDate"
        />
        <el-button type="primary" class="m-2" @click="onSubmit">{{
          $t("arg.query")
        }}</el-button>
        <el-button type="success" class="m-2" @click="onDownload">{{
          $t("arg.exportData")
        }}</el-button>
      </div>
      <div
        ref="main"
        v-if="currentLineData"
        :id="chartContainer"
        style="width: 100%; height: 400px"
      ></div>
      <el-empty v-else :description="$t('home.noData')" />
    </el-dialog>
  </div>
</template>

<style lang="scss">
.arg-container {
  background-color: #f1faf5;
  border-radius: 10px;
}

.sign-container {
  width: 100%;
  height: 400px;
}

.custom-date-picker-popper {
  z-index: 9999 !important; /* 设置你需要的层级 */
}
</style>
